import React, { ReactNode, createContext, useContext, useMemo } from 'react';

interface Navigation {
  menu: (MenuItem & { active: boolean })[];
  slug?: string;
}

const NavigationContext = createContext<Navigation>({
  menu: [],
});

export interface PageContext {
  frontmatter?: {
    cover: string;
    description: string;
    menus: Record<string, { identifier: string }>;
    meta?: {
      description?: string;
      title?: string;
    };
    promoted?: boolean;
    published?: boolean;
    publishedAt?: string;
    updatedAt?: string;
    title?: string;
    subtitle?: string;
  };
  menus: Record<string, MenuItem[]>;
  slug: string;
}

interface MenuItem {
  identifier: string;
  title: string;
  url: string;
  weight: number;
}

function getPageMenuItem(pageContext: PageContext) {
  return pageContext.menus[pageContext.langKey].find(
    (item) =>
      (pageContext.slug.length > 4 ? pageContext.slug.substring(4) : '/') ===
      `${item.url.substring(4)}/`
  );
}

function getPageTranslation(pageContext: PageContext) {
  const menuItem = getPageMenuItem(pageContext);
  return (
    (menuItem &&
      pageContext.menus[
        pageContext.langKey === LangCode.EN ? LangCode.FR : LangCode.EN
      ].find(
        (translation) =>
          translation.identifier.split('_')[0] ===
          menuItem.identifier.split('_')[0]
      )) ||
    null
  );
}

interface Props {
  children: ReactNode;
  pageContext: PageContext;
}

function NavigationProvider({ children, pageContext }: Props): JSX.Element {
  const hasSlug = pageContext.hasOwnProperty('slug');
  const slug = hasSlug ? pageContext.slug.replace(/^\/(.+)\/$/g, '$1') : null;
  const value = useMemo(
    () => ({
      menu: pageContext.menus.main.map((item) => ({
        ...item,
        active: item.url.replace(/^\/?(.+)\/?$/g, '$1') === slug,
        slug: slug,
      })),
    }),
    [pageContext]
  );
  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

const useNavigation = () => useContext(NavigationContext);

export { NavigationContext, useNavigation };
export default NavigationProvider;
